import { Flow, FlowStep, HttpMethod } from '../models';

const step = (
  id: string,
  name: string,
  method: HttpMethod,
  path: string,
  extra: Partial<FlowStep> = {}
): FlowStep => ({
  id,
  name,
  method,
  path,
  headers: {},
  bodyMode: 'none',
  body: '',
  fields: [],
  captures: [],
  expectStatus: '',
  enabled: true,
  ...extra,
});

const DRIVER_AUTH = {
  Authorization: 'Bearer {{driverToken}}',
  'x-logistic-operator-id': '{{operatorId}}',
  'x-driver-id': '{{driverId}}',
};

/**
 * Ciclo real de retiro flex, el mismo que corre en produccion:
 *
 *   PENDING --[transicion Retiro Flex]--> PICKED_UP --[control de arribo]-->
 *   ARRIBADO --[ruta + start]--> ON_ROUTE --[entrega con firma]--> DELIVERED
 *
 * Dos cosas que no son intercambiables:
 *  - el control de arribo va por /transitions/execute-batch con el token del
 *    CONDUCTOR y el header `x-logistic-operator` SIN `-id`. Con el token de
 *    usuario el back responde que el conductor solicitante no existe.
 *  - esa transicion pide autorizacion: hasta que no se autoriza el pedido no se
 *    mueve, y el paso pasa desapercibido porque responde 200 igual.
 *
 * El armado de la visita asume UN pedido por ruta, que es el caso de esta
 * prueba: toma la parada de `route.1` de lo que devuelve /routes/generate.
 */
const flexFlow: Flow = {
  id: 'seed-flex-pickup',
  name: 'Retiro flex - ciclo completo',
  description:
    'Crea un pedido demo sobre el comercio y la sucursal configurados, aplica retiro flex, controla arribo con autorizacion, rutea, inicia y entrega con firma. Ojo con el paso de la solicitud pendiente: toma la PRIMERA de la lista, que en un ambiente con movimiento puede no ser la de este pedido.',
  variables: {
    operatorId: 'a667974d-6dbb-433a-9649-0df69a6de4d3',
    commerceId: 'abb65c81-c0ee-46ac-b6fb-84ebfc853167',
    branchId: 'f1b29529-e267-4488-9432-b1bc978a7183',
    driverEmail: '',
    driverPassword: '',
    countryId: '',
    stateId: '',
    cityId: '',
    neighborhoodId: '',
    sizeId: '',
    timeSlotId: '',
    complexityId: '',
    channelId: '',
    serviceFlexId: '',
    pendingStatusId: '',
    retiroFlexTransitionId: '',
    arriboTransitionId: '',
    entregarTransitionId: '',
  },
  steps: [
    step('driver-login', 'Login del conductor', 'POST', '/auth/login', {
      bodyMode: 'json',
      body: [
        '{',
        '  "email": "{{driverEmail}}",',
        '  "password": "{{driverPassword}}"',
        '}',
      ].join('\n'),
      captures: [
        { name: 'driverToken', path: 'accessToken' },
        { name: 'driverId', path: 'driver.id' },
      ],
    }),
    step(
      'get-branch',
      'Leer la sucursal (direccion de origen)',
      'GET',
      '/branches/{{branchId}}',
      { captures: [{ name: 'branchAddressId', path: 'addressId' }] }
    ),
    step(
      'get-commerce',
      'Leer el comercio (direccion de destino)',
      'GET',
      '/commerces/{{commerceId}}',
      { captures: [{ name: 'commerceAddressId', path: 'addressId' }] }
    ),
    step('create-shipping', 'Crear pedido flex', 'POST', '/shipping', {
      bodyMode: 'json',
      body: [
        '{',
        '  "originAddressId": "{{branchAddressId}}",',
        '  "destinationAddressId": "{{commerceAddressId}}",',
        '  "logisticOperatorId": "{{operatorId}}",',
        '  "commerceId": "{{commerceId}}",',
        '  "branchId": "{{branchId}}",',
        '  "recipient": "Cliente Flex Demo",',
        '  "recipientPhoneNumber": "987654321",',
        '  "dateToDelivery": "2027-01-15T18:00:00.000Z",',
        '  "logisticOperatorShippingNumber": "FLEX-{{stamp}}",',
        '  "commerceShippingNumber": "FLEX-{{stamp}}",',
        '  "serviceWithOperatorId": "{{serviceFlexId}}",',
        '  "statusId": "{{pendingStatusId}}",',
        '  "weight": 1.5,',
        '  "pickupDetails": "demo",',
        '  "deliveryDetails": "demo",',
        '  "originCountryId": "{{countryId}}",',
        '  "originStateOrDepartmentId": "{{stateId}}",',
        '  "originCityId": "{{cityId}}",',
        '  "originNeighborhoodId": "{{neighborhoodId}}",',
        '  "destinationCountryId": "{{countryId}}",',
        '  "destinationStateOrDepartmentId": "{{stateId}}",',
        '  "destinationCityId": "{{cityId}}",',
        '  "destinationNeighborhoodId": "{{neighborhoodId}}",',
        '  "sizeId": "{{sizeId}}",',
        '  "timeSlotId": "{{timeSlotId}}",',
        '  "complexityId": "{{complexityId}}",',
        '  "channelId": "{{channelId}}"',
        '}',
      ].join('\n'),
      captures: [{ name: 'flexNumber', path: 'number' }],
      expectStatus: '201',
    }),
    step('apply-flex-pickup', 'Aplicar retiro flex', 'POST', '/transitions/apply', {
      bodyMode: 'json',
      body: [
        '{',
        '  "transitionId": "{{retiroFlexTransitionId}}",',
        '  "shippingNumber": {{flexNumber}}',
        '}',
      ].join('\n'),
      expectStatus: '200,201',
    }),
    step(
      'arrival-request',
      'Control de arribo: lo solicita el conductor',
      'POST',
      '/transitions/execute-batch/{{arriboTransitionId}}',
      {
        headers: {
          Authorization: 'Bearer {{driverToken}}',
          'x-logistic-operator': '{{operatorId}}',
          'x-driver-id': '{{driverId}}',
        },
        bodyMode: 'json',
        body: ['{', '  "shippingNumbers": [{{flexNumber}}]', '}'].join('\n'),
        expectStatus: '200',
      }
    ),
    step(
      'arrival-pending',
      'Buscar la solicitud pendiente (revisar que sea la del pedido)',
      'GET',
      '/authorization-transitions/pending?limit=50',
      { captures: [{ name: 'authorizationId', path: 'items.0.id' }] }
    ),
    step(
      'arrival-authorize',
      'Autorizar el control de arribo',
      'PATCH',
      '/authorization-transitions/{{authorizationId}}/set-authorized',
      { bodyMode: 'json', body: '{}', expectStatus: '200,201' }
    ),
    step(
      'find-routable',
      'Buscar el pedido ruteable',
      'GET',
      '/shipping/find-ruteable/{{flexNumber}}?mode=NUMBER',
      {
        headers: { 'x-logistic-operator-id': '{{operatorId}}' },
        captures: [{ name: 'find-routable', path: '' }],
      }
    ),
    step('generate-route', 'Generar la ruta', 'POST', '/routes/generate', {
      headers: { 'x-logistic-operator-id': '{{operatorId}}' },
      bodyMode: 'json',
      body: [
        '{',
        '  "shippings": [{{ruteable}}],',
        '  "scheduled": "2027-01-15T13:00:00.000Z"',
        '}',
      ].join('\n'),
      captures: [
        { name: 'stopLat', path: 'route.1.lat' },
        { name: 'stopLong', path: 'route.1.long' },
        { name: 'stopOrder', path: 'route.1.order' },
        { name: 'totalSeconds', path: 'totalSeconds' },
      ],
      expectStatus: '200,201',
    }),
    step('create-route', 'Crear la ruta', 'POST', '/routes', {
      headers: { 'x-logistic-operator-id': '{{operatorId}}' },
      bodyMode: 'json',
      body: [
        '{',
        '  "scheduled": "2027-01-15T13:00:00.000Z",',
        '  "totalSeconds": {{totalSeconds}},',
        '  "logisticOperatorId": "{{operatorId}}",',
        '  "driverId": "{{driverId}}",',
        '  "visits": [',
        '    {',
        '      "shippingNumber": {{flexNumber}},',
        '      "type": "DELIVERY",',
        '      "scanOrder": 1,',
        '      "scheduledFrom": "2027-01-15T13:00:00.000Z",',
        '      "scheduledTo": "2027-01-15T15:00:00.000Z",',
        '      "lat": {{stopLat}},',
        '      "long": {{stopLong}},',
        '      "order": {{stopOrder}}',
        '    }',
        '  ]',
        '}',
      ].join('\n'),
      captures: [
        { name: 'routeId', path: 'route.id' },
        { name: 'visitId', path: 'visits.0.id' },
      ],
      expectStatus: '200,201',
    }),
    step(
      'start-route',
      'Iniciar la ruta',
      'PATCH',
      '/routes/{{routeId}}/start',
      {
        headers: DRIVER_AUTH,
        bodyMode: 'json',
        body: ['{', '  "driverId": "{{driverId}}"', '}'].join('\n'),
        expectStatus: '200,201',
      }
    ),
    step(
      'deliver',
      'Entregar con firma',
      'POST',
      '/order-action/{{visitId}}/finalize',
      {
        headers: DRIVER_AUTH,
        bodyMode: 'multipart',
        fields: [
          { name: 'signature_{{flexNumber}}', value: '', kind: 'png' },
          {
            name: 'transitionId',
            value: '{{entregarTransitionId}}',
            kind: 'text',
          },
          { name: 'logisticOperatorId', value: '{{operatorId}}', kind: 'text' },
          { name: 'latitude', value: '{{stopLat}}', kind: 'text' },
          { name: 'longitude', value: '{{stopLong}}', kind: 'text' },
          { name: 'visitedAt', value: '2027-01-15T14:00:00', kind: 'text' },
          {
            name: 'shippings',
            value:
              '[{"shippingNumber":{{flexNumber}},"scanOrder":1,"scannedDt":"2027-01-15T14:00:00.000Z","status":"DELIVERED","receiverName":"Cliente Flex","receiverDocument":"70111222"}]',
            kind: 'text',
          },
        ],
        expectStatus: '200,201',
      }
    ),
  ],
  links: [
    { label: 'Pedido flex', path: '/business/shipping/{{flexNumber}}' },
    {
      label: 'Listado del comercio de prueba',
      path: '/business/shippings?search=FLEX-{{stamp}}',
    },
  ],
};

const catalogFlow: Flow = {
  id: 'seed-quick-check',
  name: 'Chequeo rapido del back',
  description:
    'Solo lecturas. Sirve para ver de un vistazo si la API responde despues de un deploy.',
  variables: { operatorId: 'a667974d-6dbb-433a-9649-0df69a6de4d3' },
  steps: [
    step('shipping-list', 'Listado de pedidos', 'GET', '/shipping?page=1&limit=1'),
    step(
      'operator-services',
      'Servicios del operador',
      'GET',
      '/service-with-operator?limit=200&logisticOperatorId={{operatorId}}'
    ),
    step('shipping-sizes', 'Catalogo de tamanos', 'GET', '/shipping-sizes'),
    step('shipping-statuses', 'Estados de pedido', 'GET', '/shipping-statuses'),
  ],
  links: [{ label: 'Listado de envios', path: '/business/shippings' }],
};

export const SEED_FLOWS: Flow[] = [flexFlow, catalogFlow];
